/**
 * Ida y vuelta: mismo trayecto de regreso → tarifa/distancia/tiempo de ida × 2.
 * Idempotente: no vuelve a duplicar si el estimate ya marcó roundTripApplied.
 * En mapa: ida (coords) + vuelta (returnCoords). En viaje: roundTripLeg outbound|return.
 */

export const isRoundTrip = (value) => value === true || value === 1 || value === '1' || value === 'true';

/** Tramo activo del viaje ida/vuelta (null si no es round trip). */
export const isRoundTripReturn = (booking) =>
  !!(booking && isRoundTrip(booking.roundTrip) && booking.roundTripLeg === 'return');

export const isRoundTripOutbound = (booking) =>
  !!(booking && isRoundTrip(booking.roundTrip) && booking.roundTripLeg !== 'return');

/** Invierte una polilínea {latitude,longitude}[] para dibujar / navegar la vuelta. */
export const reverseRouteCoords = (coords) => {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  return coords.map((p) => ({ ...p })).reverse();
};

/**
 * Paradas intermedias en orden de la vuelta (última parada de la ida → primera).
 * Soporta {lat,lng,add} del booking.
 */
export const reverseWaypoints = (waypoints) => {
  if (!Array.isArray(waypoints) || waypoints.length === 0) return null;
  const valid = waypoints.filter(
    (p) => p && p.lat != null && p.lng != null && !isNaN(Number(p.lat)) && !isNaN(Number(p.lng))
  );
  if (!valid.length) return null;
  return valid.map((p) => ({ ...p })).reverse();
};

/** Waypoints activos según el tramo (ida = original, vuelta = invertidos). */
export const activeWaypointsForLeg = (booking) => {
  if (!booking) return null;
  const stops = Array.isArray(booking.waypoints) ? booking.waypoints : null;
  if (!stops || !stops.length) return null;
  if (isRoundTripReturn(booking)) {
    if (Array.isArray(booking.returnWaypoints) && booking.returnWaypoints.length) {
      return booking.returnWaypoints;
    }
    return reverseWaypoints(stops);
  }
  return stops;
};

/** Destino de navegación del tramo actual. */
export const activeDestinationForLeg = (booking) => {
  if (!booking) return null;
  if (isRoundTripReturn(booking)) return booking.pickup || null;
  return booking.drop || null;
};

export const applyRoundTripMultiplier = (amount, roundTrip) => {
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return 0;
  return isRoundTrip(roundTrip) ? n * 2 : n;
};

const numOr = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Devuelve un estimate listo para persistir.
 * Duplica estimateFare, fareCost, convenience_fees, estimateDistance, estimateTime.
 */
export const applyRoundTripToEstimate = (estimate, roundTrip) => {
  if (!estimate || typeof estimate !== 'object') return estimate;
  if (!isRoundTrip(roundTrip)) {
    return { ...estimate, roundTrip: false, roundTripApplied: false };
  }
  if (estimate.roundTripApplied === true) {
    return { ...estimate, roundTrip: true };
  }
  return {
    ...estimate,
    estimateFare: numOr(estimate.estimateFare) * 2,
    fareCost: numOr(estimate.fareCost) * 2,
    convenience_fees: numOr(estimate.convenience_fees) * 2,
    estimateDistance: numOr(estimate.estimateDistance) * 2,
    estimateTime: numOr(estimate.estimateTime) * 2,
    roundTrip: true,
    roundTripApplied: true,
  };
};

/** Precio mínimo de oferta cuando hay ida y vuelta. */
export const roundTripMinimum = (minimumPrice, roundTrip) =>
  applyRoundTripMultiplier(minimumPrice, roundTrip);

/** Monto a cobrar / comparar con billetera (oferta manual o estimate RT). */
export const roundTripPayable = ({ offerFare, estimateFare, roundTrip }) => {
  const offer = numOr(offerFare);
  if (offer > 0) return offer;
  return applyRoundTripMultiplier(estimateFare, roundTrip);
};
